import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';

interface Usuario {
  id: number;
  nome: string;
  email: string;
  funcao: string;
  ativo: boolean;
  criado_em: string;
  // Campos específicos para motoristas
  cnh?: string | null;
  cnh_validade?: string | null;
  cpf?: string | null;
  rg?: string | null;
  pis?: string | null;
  ctps?: string | null;
  matricula?: string | null;
  celular?: string | null;
  sexo?: string | null;
  data_admissao?: string | null;
  motorista_emergencial?: boolean;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const Usuarios: React.FC = () => {
  const { user } = useAuthStore();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const hasLoadedRef = useRef(false);
  
  // Garantir que usuarios seja sempre um array
  const safeUsuarios = Array.isArray(usuarios) ? usuarios : [];
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    funcao: 'motorista',
    // Campos específicos para motoristas
    cnh: '',
    cnh_validade: '',
    cpf: '',
    rg: '',
    pis: '',
    ctps: '',
    matricula: '',
    celular: '',
    sexo: '',
    data_admissao: '',
    motorista_emergencial: false
  });
  const [editFormData, setEditFormData] = useState({
    nome: '',
    email: '',
    funcao: 'motorista',
    novaSenha: '',
    // Campos específicos para motoristas
    cnh: '',
    cnh_validade: '',
    cpf: '',
    rg: '',
    pis: '',
    ctps: '',
    matricula: '',
    celular: '',
    sexo: '',
    data_admissao: '',
    motorista_emergencial: false
  });

  useEffect(() => {
    if (!user || user.funcao !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem gerenciar usuários.');
      return;
    }

    fetchUsuarios(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage]);

  const fetchUsuarios = async (page = currentPage, options?: { silent?: boolean }) => {
    try {
      if (!hasLoadedRef.current && !options?.silent) {
        setLoading(true);
      }

      const response = await api.get('/usuarios', {
        params: { page, limit: itemsPerPage }
      });

      const usuariosData = response.data?.users || [];
      const paginationData = response.data?.pagination;

      setUsuarios(usuariosData);
      setPagination({
        page: paginationData?.page ?? page,
        limit: paginationData?.limit ?? itemsPerPage,
        total: paginationData?.total ?? usuariosData.length,
        totalPages: paginationData?.totalPages ?? 1
      });
      hasLoadedRef.current = true;
    } catch (error: any) {
      console.error('❌ Erro completo:', error);

      if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      } else if (!hasLoadedRef.current) {
        toast.error(`Erro ao carregar usuários: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Preparar dados para envio
      const dadosEnvio: any = {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        funcao: formData.funcao
      };

      // Adicionar campos de motorista se a função for motorista
      if (formData.funcao === 'motorista') {
        dadosEnvio.cnh = formData.cnh || null;
        dadosEnvio.cnh_validade = formData.cnh_validade || null;
        dadosEnvio.cpf = formData.cpf || null;
        dadosEnvio.rg = formData.rg || null;
        dadosEnvio.pis = formData.pis || null;
        dadosEnvio.ctps = formData.ctps || null;
        dadosEnvio.matricula = formData.matricula || null;
        dadosEnvio.celular = formData.celular || null;
        dadosEnvio.sexo = formData.sexo || null;
        dadosEnvio.data_admissao = formData.data_admissao || null;
        dadosEnvio.motorista_emergencial = formData.motorista_emergencial || false;
      }

      console.log('🔍 Criando usuário:', dadosEnvio);
      const response = await api.post('/usuarios', dadosEnvio);
      console.log('✅ Usuário criado:', response.data);
      
      toast.success('Usuário criado com sucesso!');
      setShowCreateForm(false);
      setFormData({
        nome: '',
        email: '',
        senha: '',
        funcao: 'motorista',
        cnh: '',
        cnh_validade: '',
        cpf: '',
        rg: '',
        pis: '',
        ctps: '',
        matricula: '',
        celular: '',
        sexo: '',
        data_admissao: '',
        motorista_emergencial: false
      });
      if (currentPage === 1) {
        fetchUsuarios(1, { silent: true });
      } else {
        setCurrentPage(1);
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar usuário:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Dados:', error.response?.data);
      
      if (error.response?.status === 400) {
        toast.error(`Erro de validação: ${error.response.data.error || 'Dados inválidos'}`);
      } else if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem criar usuários.');
      } else {
        toast.error(`Erro ao criar usuário: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleToggleAtivo = async (userId: number, ativo: boolean) => {
    try {
      await api.put(`/usuarios/${userId}/ativar`, { ativo: !ativo });
      toast.success(`Usuário ${ativo ? 'desativado' : 'ativado'} com sucesso!`);
      fetchUsuarios(currentPage, { silent: true });
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
      console.error('Erro:', error);
    }
  };

  const handleEditUsuario = async (userId: number) => {
    try {
      const response = await api.get(`/usuarios/${userId}`);
      const usuario = response.data.user;
      
      setEditFormData({
        nome: usuario.nome,
        email: usuario.email,
        funcao: usuario.funcao,
        novaSenha: '',
        cnh: usuario.cnh || '',
        cnh_validade: usuario.cnh_validade ? new Date(usuario.cnh_validade).toISOString().split('T')[0] : '',
        cpf: usuario.cpf || '',
        rg: usuario.rg || '',
        pis: usuario.pis || '',
        ctps: usuario.ctps || '',
        matricula: usuario.matricula || '',
        celular: usuario.celular || '',
        sexo: usuario.sexo || '',
        data_admissao: usuario.data_admissao ? new Date(usuario.data_admissao).toISOString().split('T')[0] : '',
        motorista_emergencial: usuario.motorista_emergencial || false
      });
      setEditingUserId(userId);
      setShowEditForm(true);
      setShowCreateForm(false);
    } catch (error: any) {
      console.error('❌ Erro ao buscar usuário:', error);
      if (error.response?.status === 404) {
        toast.error('Usuário não encontrado');
      } else {
        toast.error(`Erro ao carregar dados do usuário: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleUpdateUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUserId) return;

    try {
      const updateData: any = {
        nome: editFormData.nome,
        email: editFormData.email,
        funcao: editFormData.funcao
      };

      // Adicionar campos de motorista se a função for motorista
      if (editFormData.funcao === 'motorista') {
        updateData.cnh = editFormData.cnh || null;
        updateData.cnh_validade = editFormData.cnh_validade || null;
        updateData.cpf = editFormData.cpf || null;
        updateData.rg = editFormData.rg || null;
        updateData.pis = editFormData.pis || null;
        updateData.ctps = editFormData.ctps || null;
        updateData.matricula = editFormData.matricula || null;
        updateData.celular = editFormData.celular || null;
        updateData.sexo = editFormData.sexo || null;
        updateData.data_admissao = editFormData.data_admissao || null;
        updateData.motorista_emergencial = editFormData.motorista_emergencial || false;
      }

      // Se uma nova senha foi fornecida, atualizar senha separadamente
      if (editFormData.novaSenha && editFormData.novaSenha.length > 0) {
        if (editFormData.novaSenha.length < 6) {
          toast.error('A senha deve ter pelo menos 6 caracteres');
          return;
        }
        // Atualizar senha separadamente
        await api.put(`/usuarios/${editingUserId}/senha`, {
          nova_senha: editFormData.novaSenha
        });
      }

      // Atualizar dados do usuário
      const response = await api.put(`/usuarios/${editingUserId}`, updateData);
      console.log('✅ Usuário atualizado:', response.data);
      
      toast.success('Usuário atualizado com sucesso!');
      setShowEditForm(false);
      setEditingUserId(null);
      setEditFormData({
        nome: '',
        email: '',
        funcao: 'motorista',
        novaSenha: '',
        cnh: '',
        cnh_validade: '',
        cpf: '',
        rg: '',
        pis: '',
        ctps: '',
        matricula: '',
        celular: '',
        sexo: '',
        data_admissao: '',
        motorista_emergencial: false
      });
      fetchUsuarios(currentPage, { silent: true });
    } catch (error: any) {
      console.error('❌ Erro ao atualizar usuário:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Dados:', error.response?.data);
      
      if (error.response?.status === 400) {
        toast.error(`Erro de validação: ${error.response.data.error || 'Dados inválidos'}`);
      } else if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem editar usuários.');
      } else if (error.response?.status === 404) {
        toast.error('Usuário não encontrado');
      } else {
        toast.error(`Erro ao atualizar usuário: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingUserId(null);
    setEditFormData({
      nome: '',
      email: '',
      funcao: 'motorista',
      novaSenha: ''
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'motorista': return 'bg-blue-100 text-blue-800';
      case 'familia': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'motorista': return 'Motorista';
      case 'familia': return 'Família';
      default: return role;
    }
  };

  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalPages = pagination.totalPages;
  const startIndex = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Usuários</h1>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setShowEditForm(false);
            setEditingUserId(null);
          }}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Novo Usuário
        </button>
      </div>

      {/* Formulário de edição */}
      {showEditForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Editar Usuário</h2>
          <form onSubmit={handleUpdateUsuario}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={editFormData.nome}
                  onChange={(e) => setEditFormData({...editFormData, nome: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nova Senha (deixe em branco para não alterar)
                </label>
                <input
                  type="password"
                  value={editFormData.novaSenha}
                  onChange={(e) => setEditFormData({...editFormData, novaSenha: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Deixe em branco para manter a senha atual"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função
                </label>
                <select
                  value={editFormData.funcao}
                  onChange={(e) => setEditFormData({...editFormData, funcao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="motorista">Motorista</option>
                  <option value="familia">Família</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>

            {/* Campos específicos para motoristas */}
            {editFormData.funcao === 'motorista' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CNH
                    </label>
                    <input
                      type="text"
                      value={editFormData.cnh}
                      onChange={(e) => setEditFormData({...editFormData, cnh: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validade
                    </label>
                    <input
                      type="date"
                      value={editFormData.cnh_validade}
                      onChange={(e) => setEditFormData({...editFormData, cnh_validade: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF
                    </label>
                    <input
                      type="text"
                      value={editFormData.cpf}
                      onChange={(e) => setEditFormData({...editFormData, cpf: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RG
                    </label>
                    <input
                      type="text"
                      value={editFormData.rg}
                      onChange={(e) => setEditFormData({...editFormData, rg: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PIS
                    </label>
                    <input
                      type="text"
                      value={editFormData.pis}
                      onChange={(e) => setEditFormData({...editFormData, pis: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CTPS
                    </label>
                    <input
                      type="text"
                      value={editFormData.ctps}
                      onChange={(e) => setEditFormData({...editFormData, ctps: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Matrícula
                    </label>
                    <input
                      type="text"
                      value={editFormData.matricula}
                      onChange={(e) => setEditFormData({...editFormData, matricula: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Celular
                    </label>
                    <input
                      type="text"
                      value={editFormData.celular}
                      onChange={(e) => setEditFormData({...editFormData, celular: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sexo
                    </label>
                    <select
                      value={editFormData.sexo}
                      onChange={(e) => setEditFormData({...editFormData, sexo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Feminino">Feminino</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admissão
                    </label>
                    <input
                      type="date"
                      value={editFormData.data_admissao}
                      onChange={(e) => setEditFormData({...editFormData, data_admissao: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editFormData.motorista_emergencial}
                      onChange={(e) => setEditFormData({...editFormData, motorista_emergencial: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Motorista Emergencial</span>
                  </label>
                </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Salvar Alterações
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulário de criação */}
      {showCreateForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Novo Usuário</h2>
          <form onSubmit={handleCreateUsuario}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <input
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({...formData, senha: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função
                </label>
                <select
                  value={formData.funcao}
                  onChange={(e) => setFormData({...formData, funcao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="motorista">Motorista</option>
                  <option value="familia">Família</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>

            {/* Campos específicos para motoristas */}
            {formData.funcao === 'motorista' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CNH
                    </label>
                    <input
                      type="text"
                      value={formData.cnh}
                      onChange={(e) => setFormData({...formData, cnh: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validade
                    </label>
                    <input
                      type="date"
                      value={formData.cnh_validade}
                      onChange={(e) => setFormData({...formData, cnh_validade: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF
                    </label>
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RG
                    </label>
                    <input
                      type="text"
                      value={formData.rg}
                      onChange={(e) => setFormData({...formData, rg: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PIS
                    </label>
                    <input
                      type="text"
                      value={formData.pis}
                      onChange={(e) => setFormData({...formData, pis: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CTPS
                    </label>
                    <input
                      type="text"
                      value={formData.ctps}
                      onChange={(e) => setFormData({...formData, ctps: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Matrícula
                    </label>
                    <input
                      type="text"
                      value={formData.matricula}
                      onChange={(e) => setFormData({...formData, matricula: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Celular
                    </label>
                    <input
                      type="text"
                      value={formData.celular}
                      onChange={(e) => setFormData({...formData, celular: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sexo
                    </label>
                    <select
                      value={formData.sexo}
                      onChange={(e) => setFormData({...formData, sexo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Feminino">Feminino</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admissão
                    </label>
                    <input
                      type="date"
                      value={formData.data_admissao}
                      onChange={(e) => setFormData({...formData, data_admissao: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.motorista_emergencial}
                      onChange={(e) => setFormData({...formData, motorista_emergencial: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Motorista Emergencial</span>
                  </label>
                </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Criar Usuário
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({
                    nome: '',
                    email: '',
                    senha: '',
                    funcao: 'motorista',
                    cnh: '',
                    cnh_validade: '',
                    cpf: '',
                    rg: '',
                    pis: '',
                    ctps: '',
                    matricula: '',
                    celular: '',
                    sexo: '',
                    data_admissao: '',
                    motorista_emergencial: false
                  });
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de usuários */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Tabela responsiva */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Função
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Criado em
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
             <tbody className="bg-white divide-y divide-gray-200">
               {safeUsuarios.map((usuario) => (
                 <tr key={usuario.id} className="hover:bg-gray-50">
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                     {usuario.nome}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                     {usuario.email}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(usuario.funcao)}`}>
                       {getRoleLabel(usuario.funcao)}
                     </span>
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${usuario.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                       {usuario.ativo ? 'Ativo' : 'Inativo'}
                     </span>
                   </td>
                   <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                     {new Date(usuario.criado_em).toLocaleDateString('pt-BR')}
                   </td>
                   <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                     <div className="flex flex-col sm:flex-row gap-2">
                       <button
                         onClick={() => handleToggleAtivo(usuario.id, usuario.ativo)}
                         className={`text-xs sm:text-sm ${usuario.ativo ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                       >
                         {usuario.ativo ? 'Desativar' : 'Ativar'}
                       </button>
                       <button
                         onClick={() => handleEditUsuario(usuario.id)}
                         className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                       >
                         Editar
                       </button>
                     </div>
                   </td>
                 </tr>
               ))}
                          </tbody>
           </table>
         </div>
         
         {safeUsuarios.length === 0 && (
           <div className="text-center py-8 text-gray-500">
             Nenhum usuário encontrado.
           </div>
         )}

         {pagination.total > 0 && totalPages > 1 && (
           <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
             <div className="flex items-center justify-between">
               <div className="flex-1 flex justify-between sm:hidden">
                 <button
                   onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                   disabled={currentPage === 1}
                   className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Anterior
                 </button>
                 <button
                   onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                   disabled={currentPage === totalPages}
                   className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   Próxima
                 </button>
               </div>
               <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                 <div>
                   <p className="text-sm text-gray-700">
                     Mostrando <span className="font-medium">{startIndex}</span> até{' '}
                     <span className="font-medium">{endIndex}</span> de{' '}
                     <span className="font-medium">{pagination.total}</span> usuários
                   </p>
                 </div>
                 <div>
                   <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                     <button
                       onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                       disabled={currentPage === 1}
                       className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       ←
                     </button>
                     {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                       if (
                         page === 1 ||
                         page === totalPages ||
                         (page >= currentPage - 1 && page <= currentPage + 1)
                       ) {
                         return (
                           <button
                             key={page}
                             onClick={() => setCurrentPage(page)}
                             className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                               currentPage === page
                                 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                 : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                             }`}
                           >
                             {page}
                           </button>
                         );
                       }
                       if (page === currentPage - 2 || page === currentPage + 2) {
                         return (
                           <span
                             key={page}
                             className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                           >
                             ...
                           </span>
                         );
                       }
                       return null;
                     })}
                     <button
                       onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                       disabled={currentPage === totalPages}
                       className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       →
                     </button>
                   </nav>
                 </div>
               </div>
             </div>
           </div>
         )}
       </div>
    </div>
  );
};

export default Usuarios;
